/**
 * Whether on-device model init has failed terminally in this page load.
 *
 * ── Why module state and not an atom ──────────────────────────────────────
 *
 * Its consumer is `poseSourceRouter`, which is deliberately pure and takes this
 * as an input rather than reaching into React. Same shape as
 * `deviceCapability.ts`, for the same reason.
 *
 * ── Why it exists ─────────────────────────────────────────────────────────
 *
 * It is the router's FIRST trigger and the only one that reaches BUG-0004's
 * population — the one server-side inference recovers outright rather than
 * partially. 80% of those sessions fail on WebGL shader compilation or a lost
 * WebGL context, 7.5% have no WebAssembly, 12.5% cannot fetch the model; a POST
 * of a JPEG needs none of the three. 100% of them never complete a scan today
 * (37 of 37 across the full sweep).
 *
 * And it cannot be inferred from `deviceClass`: the capability classifier runs
 * through the same worker that just died, so these sessions land on
 * `deviceClassSource: "error"` → `deviceClass: "unknown"`, which the existing
 * fail-safe maps to the standard path. A class-keyed router routes none of them.
 *
 * ── Terminal means terminal ───────────────────────────────────────────────
 *
 * Set only where `PoseDetectionManager.initialize()` gives up — after its outer
 * `retry({retries: 3})` is exhausted, the same point it emits `error`. The inner
 * failures (`ObjectDetectionManager initialization failed after all retries.`,
 * `Model warmup failed after all retries.`) are usually rescued by a later outer
 * attempt, and counting them overstates the bug roughly 5x — the exact trap
 * BUG-0004's signature was written to avoid.
 *
 * Page-load scoped, never persisted: a WebGL context can be lost on one load and
 * fine on the next, and a stale "this device is broken" is the more dangerous of
 * the two stale verdicts.
 */

let failed = false;

/** Called where `initialize()` exhausts its outer retry and emits `error`. */
export function markModelInitFailed(): void {
  failed = true;
}

/**
 * Called on a successful initialize.
 *
 * `initialize()` is re-enterable — `initCalled` is reset on the failure path —
 * so a later attempt that succeeds must clear this. Leaving it set would route a
 * device to the server on the strength of a failure it recovered from, and the
 * on-device path is the measured-better one whenever it works.
 */
export function markModelInitSucceeded(): void {
  failed = false;
}

export function hasModelInitFailed(): boolean {
  return failed;
}

/** Test-only. */
export function __resetModelInitStateForTest(): void {
  failed = false;
}
